function normalizeRole(value = "") {
  return String(value || "").trim().toLowerCase();
}

function normalizeUpdatedAt(value) {
  if (!value) return null;
  if (typeof value === "number") return value;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? null : parsed;
}

function escapeRegExp(value = "") {
  return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function sessionSeed(session = {}) {
  const raw = String(session?.id || session?.sessionId || session?.createdAt || session?.date || "0");
  let hash = 0;
  for (let index = 0; index < raw.length; index += 1) {
    hash = ((hash << 5) - hash) + raw.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
}

function emailLocalPart(value = "") {
  const email = String(value || "").trim();
  if (!email.includes("@")) return "";
  return email.split("@")[0] || "";
}

function candidateIdentityTokens(session = {}) {
  const direct = [
    session?.client,
    session?.user,
    session?.displayName,
    session?.firstName,
    emailLocalPart(session?.userEmail),
  ]
    .map((value) => String(value || "").trim())
    .filter((value) => value && value.length >= 2);
  const splitParts = direct.flatMap((value) => value.split(/\s+/).map((part) => String(part || "").trim()));
  return Array.from(new Set([...direct, ...splitParts].filter((value) => value && value.length >= 2)));
}

export function normalizeSessionPrivacy(privacy) {
  const identityMode = String(privacy?.identityMode || "").trim().toLowerCase() === "identified"
    ? "identified"
    : "anonymous";
  const setBy = String(privacy?.setBy || "").trim().toLowerCase() === "user"
    ? "user"
    : "system";
  return {
    identityMode,
    setBy,
    updatedAt: normalizeUpdatedAt(privacy?.updatedAt),
  };
}

export function normalizeSupportConsent(consent) {
  const directCoachContact = ["requested", "allowed", "declined"].includes(
    String(consent?.directCoachContact || "").trim().toLowerCase()
  )
    ? String(consent?.directCoachContact || "").trim().toLowerCase()
    : "not_requested";
  const setBy = String(consent?.setBy || "").trim().toLowerCase() === "user"
    ? "user"
    : "system";
  return {
    directCoachContact,
    setBy,
    updatedAt: normalizeUpdatedAt(consent?.updatedAt),
  };
}

export function isAnonymousSession(session) {
  return normalizeSessionPrivacy(session?.privacy).identityMode !== "identified";
}

export function canViewerSeeSessionIdentity(session, viewerRole) {
  const role = normalizeRole(viewerRole);
  if (role === "member") return true;
  if (role === "coach" || role === "admin" || role === "superadmin") {
    return normalizeSessionPrivacy(session?.privacy).identityMode === "identified";
  }
  return false;
}

export function getAnonymousSessionLabel(session) {
  const explicitLabel = String(session?.anonymousLabel || "").trim();
  if (explicitLabel) return explicitLabel;
  const suffix = (sessionSeed(session) % 97) + 1;
  return `Anonymous User${suffix}`;
}

export function getSessionRealName(session, fallback = "Member") {
  return String(
    session?.client ||
    session?.user ||
    emailLocalPart(session?.userEmail) ||
    fallback
  ).trim() || fallback;
}

export function isOwnSession(session, viewerUserId) {
  const ownerId = String(session?.userId || "").trim();
  const viewerId = String(viewerUserId || "").trim();
  return Boolean(ownerId && viewerId && ownerId === viewerId);
}

export function getViewerSessionDisplayName(session, _viewerRole, options = {}, fallback = "Member") {
  const viewerRealName = String(options?.viewerRealName || "").trim();
  if (isOwnSession(session, options?.viewerUserId)) {
    return viewerRealName || getSessionRealName(session, fallback);
  }
  return getAnonymousSessionLabel(session);
}

export function getSessionDisplayName(session, viewerRole, fallback = "Member") {
  const role = normalizeRole(viewerRole);
  if (role === "member") return getSessionRealName(session, fallback);
  const anonymousLabel = getAnonymousSessionLabel(session);
  if ((role === "coach" || role === "admin" || role === "superadmin") && canViewerSeeSessionIdentity(session, viewerRole)) {
    const realName = getSessionRealName(session, fallback);
    return realName ? `${anonymousLabel} (${realName})` : anonymousLabel;
  }
  return anonymousLabel;
}

export function getSessionDisplayEmail(session, viewerRole) {
  if (!canViewerSeeSessionIdentity(session, viewerRole)) return "";
  return String(session?.userEmail || "").trim();
}

export function getSessionPrivacyStatus(session) {
  const privacy = normalizeSessionPrivacy(session?.privacy);
  if (privacy.identityMode === "identified") {
    return {
      ...privacy,
      label: "Identity shared",
      detail: "Coach and superadmin can see your identity for this session.",
    };
  }
  return {
    ...privacy,
    label: "Anonymous",
    detail: "Coach and superadmin can review the session, but identity stays hidden.",
  };
}

export function getSupportConsentStatus(session) {
  const consent = normalizeSupportConsent(session?.supportConsent);
  if (consent.directCoachContact === "requested") {
    return {
      ...consent,
      label: "1-on-1 support requested",
      detail: "Member asked for direct coach support. Scheduling and payment flow still comes later.",
      tone: "requested",
    };
  }
  if (consent.directCoachContact === "allowed") {
    return {
      ...consent,
      label: "Identity shared",
      detail: "Member chose identified in-platform coaching for this session.",
      tone: "allowed",
    };
  }
  if (consent.directCoachContact === "declined") {
    return {
      ...consent,
      label: "Anonymous support",
      detail: "Member chose to continue with anonymous in-platform support.",
      tone: "anonymous",
    };
  }
  return {
    ...consent,
    label: "Decision pending",
    detail: "No support preference has been chosen for this coach intervention yet.",
    tone: "pending",
  };
}

export function maskSessionIdentityText(text, session, viewerRole, options = {}) {
  const role = normalizeRole(viewerRole);
  if (isOwnSession(session, options?.viewerUserId)) return String(text || "");
  if (role === "member" && !options?.viewerUserId) return String(text || "");
  const sessionLabel = getViewerSessionDisplayName(session, viewerRole, options);
  let nextText = String(text || "");

  nextText = nextText.replace(
    /\b(Hi|Hello|Hey)\s+[^,\n]+,\s+(I(?:'|’)m|I am)\s+NIA\b/gi,
    (_, greeting, intro) => `${greeting} ${sessionLabel}, ${intro} Nia`
  );

  candidateIdentityTokens(session).forEach((token) => {
    const pattern = new RegExp(`\\b${escapeRegExp(token)}\\b`, "gi");
    nextText = nextText.replace(pattern, sessionLabel);
  });

  return nextText;
}

export function getSessionDisplayText(text, session, viewerRole, options = {}) {
  return maskSessionIdentityText(text, session, viewerRole, options);
}

export function getRenderedSessionMessage(message, session, viewerRole, options = {}) {
  const text = String(message?.text || message?.content || "");
  const sender = String(message?.sender || message?.role || "").trim().toLowerCase();
  if (sender === "nia" || sender === "assistant" || sender === "system") {
    return maskSessionIdentityText(text.replace(/\bNIA\b/g, "Nia"), session, viewerRole, options);
  }
  return text;
}

export function getCoachThreadSenderLabel(message, session, viewerRole, fallback = "Member") {
  const role = normalizeRole(message?.senderRole);
  if (role === "coach" || role === "admin" || role === "superadmin") {
    return "Coach";
  }
  return getSessionDisplayName(session, viewerRole, fallback);
}
